import React, {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import { css } from "@emotion/react";
import {
  GoogleMap,
  GoogleMapProps,
  InfoWindowF,
  useJsApiLoader,
} from "@react-google-maps/api";
import { subtleGrayscale } from "./googleMapStyle";

export type Map = google.maps.Map;

type Props = {
  defaultPosition: { lat: number; lng: number };
  googleMapsApiKey: string;
};

const useMap = ({ defaultPosition, googleMapsApiKey }: Props) => {
  // googleMapsApiKeyは自分で取得したものに差し替えてください
  const { isLoaded } = useJsApiLoader({
    id: "google-map",
    googleMapsApiKey,
  });

  const onLoad = (map: Map) => {
    map.setCenter(defaultPosition);
    map.setZoom(14);
  };

  const onUnmount = useCallback(() => {}, []);

  return { isLoaded, onLoad, onUnmount };
};

const mapContainerStyle = {
  height: "200px",
  width: "300px",
};

const googleMapOptions: google.maps.MapOptions = {
  styles: subtleGrayscale,
  disableDefaultUI: true,
};

const defaultPosition = { lat: 35.6496061, lng: 139.7883756 };

type MyGoogleMapProps = {
  onLoad: GoogleMapProps["onLoad"];
};

const MyGoogleMap: React.FC<MyGoogleMapProps> = ({ onLoad }) => {
  return (
    <GoogleMap
      options={googleMapOptions}
      mapContainerStyle={mapContainerStyle}
      onLoad={onLoad}
    >
      <InfoWindowF position={defaultPosition}>
        <div css={mapInfoStyle}>
          <a
            href="https://goo.gl/maps/1mmVPARnpN8TM97M7"
            target="_blank"
            rel="noopener noreferrer"
          >
            豊洲PIT
          </a>
        </div>
      </InfoWindowF>
    </GoogleMap>
  );
};

const Map: React.FC = () => {
  const { isLoaded, onLoad } = useMap({
    defaultPosition,
    googleMapsApiKey: process.env.REACT_APP_GOOGLE_MAPS_API_KEY || "",
  });

  return (
    <div css={mainStyle} id="map">
      <div css={containerStyle}>
        <div css={upperTitleWrapperStyle}>
          <div css={borderStyle}></div>
          <div css={titleStyle}>会場情報</div>
          <div css={borderStyle}></div>
        </div>
        <div css={subTitleStyle}>TOYOSU PIT：東京都江東区豊洲 6-1-23</div>
        <div css={mapContainer}>
          <div css={mapWrapperStyle}>
            {isLoaded ? <MyGoogleMap onLoad={onLoad} /> : "loading"}
          </div>
        </div>
      </div>
    </div>
  );
};

const primaryColor = "#DD6B74";

const mainStyle = css`
  width: 100%;
  display: flex;
  justify-content: center;
  background-color: #fff;
`;

const containerStyle = css`
  width: 100%;
  max-width: 500px;
  padding: 20px;
`;

const mapContainer = css`
  width: 100%;
  display: flex;
  justify-content: center;
`;

const mapWrapperStyle = css`
  width: 300px;
  padding: 12.5px 10px;
  background: linear-gradient(
    to right,
    rgba(248, 204, 197, 0.63) 0%,
    rgba(250, 211, 202, 0.4) 33%,
    rgba(243, 170, 178, 0.54) 100%
  );
`;

const upperTitleWrapperStyle = css`
  display: flex;
  flex-diretion: row;
  align-items: center;
`;

const borderStyle = css`
  height: 1px;
  background-color: ${primaryColor};
  flex-grow: 1;
`;

const titleStyle = css`
  margin: 0 10px;
  font-size: 12pt;
  font-weight: 500;
`;

const subTitleStyle = css`
  margin: 5px 0 10px;
  font-size: 11pt;
  font-weight: 500;
`;

const mapInfoStyle = css`
  color: grey;
  font-size: 8pt;
`;

export default Map;
